/**
 * Brand API Client
 * Communicates with the brand service API based on OpenAPI specification
 */

import type {
  ExternalAgentToolDto,
  UpdateBrandDto,
  BrandOverridesDto,
  BrandResponseDto,
  BrandStatusResponse,
  LocationResponseDto,
  CreateLocationDto,
  OperatingHoursDto,
} from "@/types/brand";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: Record<string, string>;
}

// Auth error event for global handling
export const AUTH_ERROR_EVENT = "auth:unauthorized";

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {} } = options;

  const url = `${API_BASE_URL}${endpoint}`;

  const fetchOptions: RequestInit = {
    method,
    credentials: "include", // Include httpOnly cookies
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
  };

  if (body && method !== "GET") {
    fetchOptions.body = JSON.stringify(body);
  }

  const response = await fetch(url, fetchOptions);

  // Only treat 401 as an auth error — 403 is a business logic error
  if (response.status === 401) {
    console.error("Authentication error from API:", response.status);
    window.dispatchEvent(
      new CustomEvent(AUTH_ERROR_EVENT, {
        detail: { status: response.status },
      }),
    );
    const errorText = await response.text();
    throw new Error(`Unauthorized (${response.status}): ${errorText}`);
  }

  if (!response.ok) {
    const errorText = await response.text();
    let message: string;
    try {
      const parsed = JSON.parse(errorText);
      if (Array.isArray(parsed.message)) {
        message = parsed.message.join("\n");
      } else {
        message = parsed.message || errorText;
      }
    } catch {
      message = errorText;
    }
    throw new Error(message);
  }

  return response.json();
}

export const brandClient = {
  /**
   * Get brand and AI agent status (does not throw 404 when no brand exists).
   * `brandId` targets one brand of an aggregate session's catalogue; line
   * sessions omit it and the backend resolves the session's own brand.
   */
  async getStatus(brandId?: string): Promise<BrandStatusResponse> {
    const query = brandId ? `?brandId=${encodeURIComponent(brandId)}` : "";
    return api<BrandStatusResponse>(`/brands/status${query}`);
  },

  /**
   * Toggle AI agent paused state (kill switch). `brandId` is required for
   * aggregate sessions and names the brand whose master line to pause.
   */
  async toggleAiAgent(
    paused: boolean,
    options?: { autoReplyMessage?: string; reactivateInMinutes?: number },
    brandId?: string,
  ): Promise<BrandStatusResponse> {
    return api<BrandStatusResponse>("/brands/ai-agent-toggle", {
      method: "PATCH",
      body: { paused, ...(brandId ? { brandId } : {}), ...(paused ? options : {}) },
    });
  },

  /**
   * Get a brand for the current session (includes locations)
   */
  async getBrand(): Promise<BrandResponseDto> {
    return api<BrandResponseDto>(`/brands/current`);
  },

  /**
   * Update a brand with location operations
   */
  async updateBrand(data: UpdateBrandDto): Promise<BrandResponseDto> {
    return api<BrandResponseDto>(`/brands/current`, {
      method: "PUT",
      body: data,
    });
  },

  /**
   * The tool registry the external-agent connector exposes (K6) — the universe the
   * `externalAgent.allowedTools` allow-list picks from.
   */
  async listExternalAgentTools(): Promise<ExternalAgentToolDto[]> {
    return api<ExternalAgentToolDto[]>(`/brands/current/external-agent/tools`);
  },

  /**
   * Replace the session line's per-field brand-config overrides (independent
   * per-sucursal lines only). Whole sparse-doc replace: absent fields inherit,
   * an empty body clears everything. Returns the normalized doc as persisted.
   */
  async updateOverrides(overrides: BrandOverridesDto): Promise<{ overrides: BrandOverridesDto | null }> {
    return api<{ overrides: BrandOverridesDto | null }>(`/brands/current/overrides`, {
      method: "PUT",
      body: overrides,
    });
  },

  /* ---------- Location CRUD ---------- */

  /**
   * List all locations for the current brand
   */
  async listLocations(): Promise<LocationResponseDto[]> {
    return api<LocationResponseDto[]>("/locations");
  },

  /**
   * Create a location for the current brand
   */
  async createLocation(dto: CreateLocationDto): Promise<LocationResponseDto> {
    return api<LocationResponseDto>("/locations", {
      method: "POST",
      body: dto,
    });
  },

  /**
   * Update a location
   */
  async updateLocation(id: string, dto: Partial<CreateLocationDto>): Promise<LocationResponseDto> {
    return api<LocationResponseDto>(`/locations/${id}`, {
      method: "PUT",
      body: dto,
    });
  },

  /** Toggle the agent for one location — dedicated endpoint so it works even on POS-managed branches. */
  async setLocationAi(id: string, aiEnabled: boolean): Promise<LocationResponseDto> {
    return api<LocationResponseDto>(`/locations/${id}/ai-toggle`, {
      method: "PATCH",
      body: { aiEnabled },
    });
  },

  /**
   * Turn auto-accept on or off for one branch: orders that land awaiting acceptance are accepted —
   * and printed — on their own. Dedicated endpoint, like the AI switch, so it works on POS-managed
   * branches too.
   */
  async setLocationAutoAccept(id: string, autoAcceptOrders: boolean): Promise<LocationResponseDto> {
    return api<LocationResponseDto>(`/locations/${id}/auto-accept`, {
      method: "PATCH",
      body: { autoAcceptOrders },
    });
  },

  /**
   * Set the per-branch AI operating schedule. Sent on its own so it bypasses the POS-managed guard
   * on the update path — the schedule is a Menteo-side agent concern.
   */
  async setLocationAiSchedule(
    id: string,
    payload: { aiScheduleEnabled: boolean; aiSchedule?: OperatingHoursDto; aiOutOfHoursMessage?: string },
  ): Promise<LocationResponseDto> {
    return api<LocationResponseDto>(`/locations/${id}/ai-schedule`, {
      method: "PATCH",
      body: payload,
    });
  },

  /**
   * Delete a location
   */
  async deleteLocation(id: string): Promise<void> {
    return api(`/locations/${id}`, { method: "DELETE" });
  },

  /**
   * List the Menteo clients this brand can link/sync its locations to. Each entry carries a
   * `paymentReady` flag (has an ACTIVE Menteo payment branch).
   */
};
